import { NavLink, useLocation } from 'react-router'

import { useCapabilities } from '@/auth'
import { cn, Tooltip } from '@/design'
import { groupedNavItems, isActivePath } from '@/app/route-registry'

export interface NavListProps {
  /** Rail mode: icons only, labels in tooltips (768–1023 px). */
  readonly compact?: boolean
  readonly onNavigate?: () => void
}

/** Navigation derived from capabilities — the same component serves sidebar, rail and mobile sheet. */
export function NavList({ compact = false, onNavigate }: NavListProps) {
  const capabilities = useCapabilities()
  const { pathname } = useLocation()
  const groups = groupedNavItems(capabilities)

  return (
    <nav aria-label="Main" className="flex flex-col gap-4">
      {groups.map((group) => (
        <div key={group.group}>
          {!compact && (
            <div className="mb-1 px-2.5 text-[10.5px] font-bold tracking-[0.08em] text-[#9FB0CC] uppercase">
              {group.group}
            </div>
          )}
          <ul className="flex flex-col gap-0.5">
            {group.items.map((item) => {
              const active = isActivePath(item.path, pathname)
              const link = (
                <NavLink
                  to={item.path}
                  onClick={onNavigate}
                  aria-current={active ? 'page' : undefined}
                  aria-label={compact ? item.label : undefined}
                  className={cn(
                    'flex items-center gap-2.5 rounded-sm border border-transparent px-2.5 py-2 text-[13.5px] font-medium text-[#C9D4E6] transition-colors hover:bg-white/8 hover:text-white',
                    active && 'border-white/10 bg-white/12 text-white',
                    compact && 'justify-center px-0',
                  )}
                >
                  <item.icon aria-hidden className="size-[18px] shrink-0" />
                  {!compact && <span className="truncate">{item.label}</span>}
                </NavLink>
              )
              return (
                <li key={item.path}>
                  {compact ? (
                    <Tooltip content={item.label} side="right">
                      {link}
                    </Tooltip>
                  ) : (
                    link
                  )}
                </li>
              )
            })}
          </ul>
        </div>
      ))}
    </nav>
  )
}
